/**
 * Native Push Notifications Service
 *
 * Simple flow:
 * 1. Native (AppDelegate) receives FCM token and stores in UserDefaults
 * 2. After user logs in, JS reads token from Preferences and saves to Firebase
 * 3. Also listens for registration events in case token changes
 */

import { Capacitor } from '@capacitor/core';
import { PushNotifications, Token, PushNotificationSchema, ActionPerformed } from '@capacitor/push-notifications';
import { Preferences } from '@capacitor/preferences';
import { savePushTokenToProfile, initializePushNotifications as initWebPush } from './firebase/push-notification.service';
import { getCurrentUser } from './firebase/auth.service';
import logger from '../utils/logger';

// Module state
let isInitialized = false;
let currentToken: string | null = null;

/**
 * Initialize push notifications for native platforms
 * Sets up listeners, requests permission, and registers for push
 */
export const initializePushNotificationsNative = async (): Promise<void> => {
  if (isInitialized) {
    logger.info('Push notifications already initialized');
    return;
  }

  const platform = Capacitor.getPlatform();
  logger.info(`Initializing push notifications for platform: ${platform}`);

  // Web uses different flow
  if (platform === 'web') {
    await initWebPush();
    isInitialized = true;
    return;
  }

  // Native flow
  try {
    // Step 1: Set up listeners FIRST (before register)
    setupPushListeners();

    // Step 2: Request permission
    const permResult = await PushNotifications.requestPermissions();
    logger.info(`Push permission: ${permResult.receive}`);

    if (permResult.receive !== 'granted') {
      logger.warn('Push notification permission denied');
      return;
    }

    // Step 3: Register - this triggers the 'registration' event
    await PushNotifications.register();
    logger.info('Registered for push notifications');
    isInitialized = true;
  } catch (error) {
    logger.error('Error initializing push notifications:', error);
  }
};

/**
 * Set up push notification event listeners
 */
const setupPushListeners = (): void => {
  // Token received - save to Firebase
  PushNotifications.addListener('registration', async (token: Token) => {
    logger.info('Push token received:', token.value.substring(0, 20) + '...');
    currentToken = token.value;

    // Try to save immediately if user is logged in
    const user = getCurrentUser();
    if (user) {
      await saveTokenToFirebase(user.uid, token.value);
    } else {
      logger.info('No user yet, token will be saved after login');
    }
  });

  // Registration error
  PushNotifications.addListener('registrationError', (error) => {
    logger.error('Push registration error:', error);
  });

  // Notification received in foreground
  PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
    logger.info('Push notification received:', notification.title);
  });

  // User tapped notification
  PushNotifications.addListener('pushNotificationActionPerformed', (action: ActionPerformed) => {
    logger.info('Push notification tapped');
    if (action.notification.data) {
      const event = new CustomEvent('pushNotificationTapped', { detail: action.notification.data });
      window.dispatchEvent(event);
    }
  });
};

/**
 * Save token to Firebase
 */
const saveTokenToFirebase = async (userId: string, token: string): Promise<void> => {
  try {
    await savePushTokenToProfile(userId, token);
    logger.info('Push token saved to Firebase');
  } catch (error) {
    logger.error('Error saving push token:', error);
  }
};

/**
 * Save push token after user logs in
 * Call this after authentication completes
 *
 * Checks multiple sources for the token:
 * 1. In-memory currentToken (from JS registration event)
 * 2. Native UserDefaults via Capacitor Preferences (FCM token from Swift)
 */
export const savePendingPushToken = async (userId: string): Promise<void> => {
  const platform = Capacitor.getPlatform();

  // First try in-memory token (from JS registration event)
  if (currentToken) {
    logger.info('Saving in-memory push token for user:', userId);
    await saveTokenToFirebase(userId, currentToken);
    return;
  }

  // On native, read FCM token that Swift stored in UserDefaults
  if (platform !== 'web') {
    try {
      // Swift stores FCM token with key "fcmToken" in UserDefaults.standard
      // Capacitor Preferences reads from UserDefaults.standard
      const { value } = await Preferences.get({ key: 'fcmToken' });

      if (value) {
        logger.info('Found FCM token from native, saving for user:', userId);
        currentToken = value;
        await saveTokenToFirebase(userId, value);
        return;
      }
    } catch (error) {
      logger.error('Error reading FCM token from native:', error);
    }
  }

  logger.info('No push token available to save');
};

/**
 * Get the current push token
 */
export const getPushToken = (): string | null => currentToken;

/**
 * Check if push notifications are enabled
 */
export const arePushNotificationsEnabledNative = async (): Promise<boolean> => {
  if (Capacitor.getPlatform() === 'web') {
    return 'Notification' in window && Notification.permission === 'granted';
  }

  try {
    const result = await PushNotifications.checkPermissions();
    return result.receive === 'granted';
  } catch {
    return false;
  }
};

/**
 * Clean up on logout
 */
export const cleanupPushNotificationsNative = async (): Promise<void> => {
  if (Capacitor.getPlatform() === 'web') {
    const { cleanupPushNotifications } = await import('./firebase/push-notification.service');
    await cleanupPushNotifications();
  }
  currentToken = null;
  isInitialized = false;
  logger.info('Push notifications cleaned up');
};
